import { createRouter, createWebHistory } from 'vue-router';
// import { createRouter, createWebHashHistory } from 'vue-router'; //static build - DISABLED for clean URLs
import NotFound from './components/NotFound.vue';
import HomePage from './components/HomePage.vue';
import AboutPage from './components/AboutPage.vue';
import TimingScoring from './components/TimingScoring.vue';
import MobileRoadbookReader from './components/MobileRoadbookReader.vue';
import DownloadPage from './components/DownloadPage.vue';
import CreateCrossCountry from './components/CreateCrossCountry.vue';
import CrossCountryFIACSpecsRules from './components/CrossCountryFIACSpecsRules.vue';
import CreateRoadStage from './components/CreateRoadStage.vue';
import RoadStageFIASpecsRules from './components/RoadStageFIASpecsRules.vue';
import TimeSpeedDistance from './components/TimeSpeedDistance.vue';
import RallyRoadbook from './components/RallyRoadbook.vue';
import RallyRoadbookI18n from './components/RallyRoadbookI18n.vue';
import RallyMaps from './components/RallyMaps.vue';
import RallyTulipDiagrams from './components/RallyTulipDiagrams.vue';
import ProFeatures from './components/ProFeatures.vue';
import { ROADBOOK_CONTENT } from './content/rallyRoadbook';
import { DOWNLOAD_CONTENT, DOWNLOAD_LANGS } from './content/downloadPage';

// Download page: English at /download, translations at /{lang}/download (fr, es, it, de, nl),
// cross-linked via hreflang (x-default = English).
const DOWNLOAD_ALTERNATES = [
  { lang: 'en', path: '/download' },
  ...DOWNLOAD_LANGS.map((l) => ({ lang: l, path: `/${l}/download` })),
];
const localizedDownloadRoutes = DOWNLOAD_LANGS.map((lang) => ({
  path: `/${lang}/download`,
  component: DownloadPage,
  meta: {
    title: DOWNLOAD_CONTENT[lang].title,
    description: DOWNLOAD_CONTENT[lang].description,
    lang,
    alternates: DOWNLOAD_ALTERNATES,
  },
}));

// Bilingual pillar page: English at /rally-roadbook, translations at
// /{lang}/rally-roadbook, all cross-linked via hreflang (x-default = English).
const ROADBOOK_ALTERNATES = [
  { lang: 'en', path: '/rally-roadbook' },
  ...Object.keys(ROADBOOK_CONTENT).map((l) => ({
    lang: l,
    path: `/${l}/rally-roadbook`,
  })),
];
const localizedRoadbookRoutes = Object.entries(ROADBOOK_CONTENT).map(
  ([lang, c]) => ({
    path: `/${lang}/rally-roadbook`,
    component: RallyRoadbookI18n,
    meta: {
      title: c.title,
      description: c.description,
      lang,
      alternates: ROADBOOK_ALTERNATES,
    },
  })
);
import ManageSubscription from './components/ManageSubscription.vue';
import PrivacyPolicy from './components/PrivacyPolicy.vue';
import WechatPay from './components/WechatPay.vue';
import TermsOfUse from './components/TermsOfUse.vue';
import ResetPassword from './components/ResetPassword.vue';
import ForgotPassword from './components/ForgotPassword.vue';
import UpgradeSucceeded from './components/UpgradeSucceeded.vue';
import ActivateAccount from './components/ActivateAccount.vue';
import WhatsNew from './components/WhatsNew.vue';

const routes = [
  {
    path: '/:pathMatch(.*)*',
    component: NotFound,
    meta: {
      title: '404 - Page Not Found | Rally Navigator',
      description: 'The page you are looking for could not be found. Return to Rally Navigator to create professional rally roadbooks.',
    },
  },
  {
    path: '/',
    component: HomePage,
    meta: {
      title: 'Rally Roadbook Software – Free Trial | Rally Navigator',
      description: 'Make rally roadbooks with tulip diagrams, GPX import and FIA-standard PDF printing. Used at Baja Rally and NORRA. Windows, Mac & Linux – try it free.',
    },
  },
  {
    path: '/about',
    component: AboutPage,
    meta: {
      title: 'About Rally Navigator - Trusted by Baja Rally, NORRA & Amageza',
      description: 'Learn about Rally Navigator, the professional roadbook creation software trusted by major rallies worldwide including Baja Rally, NORRA Mexican 500, and Amageza Rally South Africa.',
    },
  },
  {
    path: '/whats-new',
    component: WhatsNew,
    meta: {
      title: "What's New in Rally Navigator — Release Notes",
      description: 'Release notes for the Rally Navigator desktop app: new roadbook features, improvements and fixes in each version. Download the latest version for Windows, Mac and Linux.',
    },
  },
  {
    path: '/timing-scoring',
    component: TimingScoring,
    meta: {
      title: 'Rally Timing & Scoring Integration - GeoTraq, RallyX, Traq303',
      description: 'Rally Navigator integrates with leading rally tracking systems including GeoTraq, RallyX, Traq303, Iritrack, and Sentinel. Export OpenRally GPX format for seamless timing and scoring.',
    },
  },
  {
    path: '/mobile-roadbook-reader',
    component: MobileRoadbookReader,
    meta: {
      title: 'Rally Roadbook Reader — Digital Roadbook App for iOS & Android',
      description: 'View and navigate digital rally roadbooks on iOS and Android. The Rally Roadbook Reader app turns your phone into a live roadbook for any event.',
    },
  },
  {
    path: '/download',
    component: DownloadPage,
    meta: {
      title: 'Download Rally Navigator - Windows, Mac & Linux Rally Software',
      description: 'Download Rally Navigator for Windows, macOS, and Linux. Free trial available. Create professional FIA-compliant rally roadbooks on your desktop.',
      lang: 'en',
      alternates: DOWNLOAD_ALTERNATES,
    },
  },
  ...localizedDownloadRoutes,
  {
    path: '/how-to-create-a-cross-country',
    component: CreateCrossCountry,
    meta: {
      title: 'How to Create Cross Country Rally Roadbook - Complete Tutorial',
      description: 'Step-by-step guide to creating professional cross-country rally roadbooks using Rally Navigator. Learn route planning, waypoint placement, and FIA-compliant roadbook generation.',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: 'Rally Navigator 2.0 - How to Make Cross Country Rally Roadbooks',
        description:
          'Step-by-step walkthrough of creating an FIA-compliant cross-country rally roadbook in Rally Navigator, from route drawing to PDF and OpenRally GPX export.',
        thumbnailUrl: 'https://i.ytimg.com/vi/H5IWI_zsBG8/hqdefault.jpg',
        uploadDate: '2023-03-30',
        duration: 'PT13M42S',
        embedUrl: 'https://www.youtube.com/embed/H5IWI_zsBG8',
        contentUrl: 'https://www.youtube.com/watch?v=H5IWI_zsBG8',
      },
    },
  },
  {
    path: '/fim-fia-roadbook-specs-rules',
    component: CrossCountryFIACSpecsRules,
    meta: {
      title: 'FIM/FIA Cross Country Roadbook Specifications & Rules',
      description: 'Complete FIM and FIA specifications for cross-country rally roadbooks. Official standards, formatting rules, and compliance requirements for professional rally navigation.',
    },
  },
  {
    path: '/how-to-create-a-road-rally',
    component: CreateRoadStage,
    meta: {
      title: 'How to Create a Rally Roadbook — Road Book Rally Guide',
      description: 'Step-by-step guide to creating a road rally roadbook: design routes, add tulip diagrams, then export a print-ready rally roadbook PDF.',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: 'Rally Navigator 2.0 - How to Make Road and Stage Rally Roadbooks',
        description:
          'How to build an FIA-compliant road and stage rally roadbook in Rally Navigator, including snap-to-road routing, tulip diagrams and print-ready PDF export.',
        thumbnailUrl: 'https://i.ytimg.com/vi/NCGJn14UefM/hqdefault.jpg',
        uploadDate: '2023-04-01',
        duration: 'PT14M26S',
        embedUrl: 'https://www.youtube.com/embed/NCGJn14UefM',
        contentUrl: 'https://www.youtube.com/watch?v=NCGJn14UefM',
      },
    },
  },
  {
    path: '/fia-roadbook-specs-rules',
    component: RoadStageFIASpecsRules,
    meta: {
      title: 'FIA Road Rally Roadbook Specifications & Rules',
      description: 'Official FIA specifications and rules for road rally and stage rally roadbooks. Standards for formatting, symbols, distance markers, and compliance requirements.',
    },
  },
  {
    path: '/rally-roadbook',
    component: RallyRoadbook,
    meta: {
      title: 'Rally Roadbook Creator — Design, Make & Print Roadbooks',
      description:
        'Roadbook creator app for cross-country, road rally and TSD. Draw your route, auto-build tulips, print a PDF roadbook and export GPX. Free trial.',
      lang: 'en',
      alternates: ROADBOOK_ALTERNATES,
    },
  },
  ...localizedRoadbookRoutes,
  {
    path: '/rally-maps',
    component: RallyMaps,
    meta: {
      title: 'Rally Route Planner — Map Your Rally Route | Rally Navigator',
      description:
        'Rally route planner for organizers. Draw your route on MapBox 3D terrain and satellite maps, then turn it into a print-ready navigation roadbook.',
    },
  },
  {
    path: '/rally-tulip-diagrams',
    component: RallyTulipDiagrams,
    meta: {
      title: 'Rally Tulip Diagrams Explained — Roadbook Tulips',
      description:
        'What is a tulip diagram? Learn how rally roadbook tulips show each junction with a dot and arrow, and how Rally Navigator draws them automatically.',
    },
  },
  {
    path: '/pro-features',
    component: ProFeatures,
    meta: {
      title: 'Commercial Rally Roadbook Licence — Pro Rally | Rally Navigator',
      description:
        'Produce rally roadbooks commercially. Pro Rally removes the Not for Commercial Use marking and adds FIA time control zones, custom headers, GPS validation and KMZ export.',
    },
  },
  {
    path: '/time-speed-distance',
    component: TimeSpeedDistance,
    meta: {
      title: 'TSD Rally Calculator & Regularity Rally Roadbooks',
      description: 'Plan Time Speed Distance (TSD) rallies, also called regularity rallies, with precise timing and checkpoint math. Build TSD roadbooks with Rally Navigator.',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: 'Rally Navigator - Time Speed Distance Roadbooks - How it Works',
        description:
          'How Time Speed Distance (TSD) and regularity rally roadbooks work in Rally Navigator, with precise timing, average-speed and checkpoint calculations.',
        thumbnailUrl: 'https://i.ytimg.com/vi/zEvr7_Jihuo/hqdefault.jpg',
        uploadDate: '2020-09-14',
        duration: 'PT9M51S',
        embedUrl: 'https://www.youtube.com/embed/zEvr7_Jihuo',
        contentUrl: 'https://www.youtube.com/watch?v=zEvr7_Jihuo',
      },
    },
  },
  {
    path: '/subscriptions',
    component: ManageSubscription,
    meta: {
      title: 'Manage Your Subscription - Update Rally Navigator',
      description:
        'Subscriptions are now managed inside the Rally Navigator app. Download the latest version of Rally Navigator to manage, link, or upgrade your subscription.',
    },
  },
  {
    path: '/wechat',
    component: WechatPay,
    meta: {
      title: 'Pay with WeChat Pay or Alipay — Rally Navigator 微信支付 支付宝',
      description:
        'Buy a 1-year Rally Navigator membership with WeChat Pay or Alipay — no credit card needed, one-time payment, activates automatically. 使用微信支付或支付宝购买 Rally Navigator 一年会员，无需信用卡，自动激活。',
    },
  },
  {
    path: '/terms-of-use',
    component: TermsOfUse,
    meta: {
      title: 'Terms of Use - Rally Navigator',
      description: 'The terms covering use of the Rally Navigator website, desktop application and mobile roadbook reader, including licensing, commercial use and ownership.',
    },
  },
  {
    path: '/privacy-policy',
    component: PrivacyPolicy,
    meta: {
      title: 'Privacy Policy - Rally Navigator',
      description: 'Rally Navigator privacy policy and data handling practices. Learn how we protect your information and maintain user privacy.',
    },
  },
  {
    path: '/activate_account/',
    component: ActivateAccount,
    meta: {
      title: 'Activate Your Account - Rally Navigator',
      description: 'Activate your Rally Navigator account to start creating professional rally roadbooks.',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: '/reset_password',
    component: ResetPassword,
    meta: {
      title: 'Reset Password - Rally Navigator',
      description: 'Reset your Rally Navigator account password.',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: '/forgot_password',
    component: ForgotPassword,
    meta: {
      title: 'Forgot Password - Rally Navigator',
      description: 'Request a password reset for your Rally Navigator account.',
      robots: 'noindex, nofollow',
    },
  },
  {
    path: '/upgraded',
    component: UpgradeSucceeded,
    meta: {
      title: 'Upgrade Successful - Rally Navigator',
      description: 'Your Rally Navigator subscription has been upgraded successfully.',
      robots: 'noindex, nofollow',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  // history: createWebHashHistory(), //static build - DISABLED for clean URLs
  routes,
  // Deep links such as /pro-features#gps-recorder must land on the section, so
  // honour the hash when there is one. The headings carry scroll-mt-[90px] to
  // clear the fixed mobile header. Everything else still starts at the top.
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { top: 0, left: 0 };
  },
});

// Derive a short breadcrumb label from the page title: cut at the first " – ",
// " — ", " - " or " | " separator to drop the tagline/brand suffix. Operates on
// the already-localized title, so localized pages get localized crumbs for free.
function breadcrumbName(title) {
  if (!title) return 'Rally Navigator';
  return title.split(/\s[|–—-]\s/)[0].trim();
}

router.beforeEach((toRoute, _fromRoute, next) => {
  // Update page title. <meta name="title"> is static in index.html, so it has
  // to be rewritten per route too or every page reports the homepage's title.
  const title =
    toRoute.meta && toRoute.meta.title
      ? toRoute.meta.title
      : 'Rally Navigator - Rally Roadbook System';
  window.document.title = title;
  updateMetaTag('title', title, 'name');

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  const description =
    toRoute.meta && toRoute.meta.description
      ? toRoute.meta.description
      : 'Create professional FIA-compliant rally roadbooks with Rally Navigator. Professional route planning, GPS tracking, and digital roadbook creation software.';
  metaDescription.setAttribute('content', description);

  // Update robots meta tag
  let metaRobots = document.querySelector('meta[name="robots"]');
  if (toRoute.meta && toRoute.meta.robots) {
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', toRoute.meta.robots);
  } else if (metaRobots) {
    metaRobots.remove();
  }

  // Update Open Graph tags
  updateMetaTag('og:title', title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:type', 'website');
  updateMetaTag('og:url', `https://www.rallynavigator.com${toRoute.path}`);
  updateMetaTag('og:site_name', 'Rally Navigator');
  updateMetaTag('og:image', 'https://www.rallynavigator.com/og-image.jpg');

  // Update Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image', 'name');
  updateMetaTag('twitter:title', title, 'name');
  updateMetaTag('twitter:description', description, 'name');
  updateMetaTag('twitter:image', 'https://www.rallynavigator.com/og-image.jpg', 'name');

  // Update canonical URL
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', `https://www.rallynavigator.com${toRoute.path}`);

  // Per-route JSON-LD structured data: a BreadcrumbList on every page except the
  // home page, plus any page-specific schema (e.g. VideoObject on how-to pages).
  // Emitted as a single script — one object, or an array when both are present.
  const graph = [];
  if (toRoute.path !== '/') {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Rally Navigator',
          item: 'https://www.rallynavigator.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: breadcrumbName(toRoute.meta && toRoute.meta.title),
          item: `https://www.rallynavigator.com${toRoute.path}`,
        },
      ],
    });
  }
  if (toRoute.meta && toRoute.meta.jsonLd) graph.push(toRoute.meta.jsonLd);

  let routeLd = document.getElementById('route-jsonld');
  if (graph.length) {
    if (!routeLd) {
      routeLd = document.createElement('script');
      routeLd.type = 'application/ld+json';
      routeLd.id = 'route-jsonld';
      document.head.appendChild(routeLd);
    }
    routeLd.textContent = JSON.stringify(graph.length === 1 ? graph[0] : graph);
  } else if (routeLd) {
    routeLd.remove();
  }

  // Page language (default English) for <html lang> + og:locale.
  const lang = (toRoute.meta && toRoute.meta.lang) || 'en';
  document.documentElement.setAttribute('lang', lang);
  // Right-to-left languages (Arabic, etc.) need dir="rtl" on <html>.
  document.documentElement.setAttribute(
    'dir',
    ['ar', 'he', 'fa', 'ur'].includes(lang) ? 'rtl' : 'ltr'
  );
  const OG_LOCALES = {
    en: 'en_US',
    de: 'de_DE',
    fr: 'fr_FR',
    nl: 'nl_NL',
    es: 'es_ES',
    it: 'it_IT',
  };
  updateMetaTag('og:locale', OG_LOCALES[lang] || 'en_US');

  // hreflang alternates for translated pages (x-default -> English).
  updateHreflang(toRoute.meta && toRoute.meta.alternates);

  next();
});

// Helper function to update meta tags
function updateMetaTag(property, content, attribute = 'property') {
  let meta = document.querySelector(`meta[${attribute}="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

// Emit <link rel="alternate" hreflang> tags for a page's language variants,
// plus an x-default pointing at the English version. Clears stale tags first.
function updateHreflang(alternates) {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  if (!alternates || !alternates.length) return;
  const base = 'https://www.rallynavigator.com';
  const add = (hreflang, path) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', `${base}${path}`);
    document.head.appendChild(link);
  };
  alternates.forEach((alt) => add(alt.lang, alt.path));
  const def = alternates.find((alt) => alt.lang === 'en') || alternates[0];
  add('x-default', def.path);
}

export default router;
